<!--
  Capture → upload → tag, the one flow every variant shares.

  Step 1 picks: the camera (live preview with a shutter when the browser allows it, the phone's own
  camera otherwise; video always through the phone's camera) or the photo library (several at once).
  Each pick is processed in the browser straight away (utils/mediaPipeline: HEIC → WebP, ≤ 2560 px,
  320 px thumbnail, EXIF capture time read and then stripped with the rest of the metadata), so a
  bad file is reported on its tile before anything is sent.
  Step 2 tags the batch and commits it in one call (two-step upload: nothing half-done reaches the
  library). With `asNote` it also writes a note that references the new items.
  The layout decides where it sits; "back" leaves without uploading.
-->
<template>
  <section class="md-capture" :class="`is-${layout}`" :aria-label="heading">
    <header class="md-capture-head">
      <button v-if="backLabel" class="md-back" @click="close">‹ {{ backLabel }}</button>
      <button v-if="step === 'tag'" class="md-iconbtn" aria-label="Back to the pictures" @click="step = 'pick'">‹</button>
      <h2 class="md-grow">{{ heading }}</h2>
    </header>

    <div v-if="step === 'pick'">
      <div class="md-seg md-capture-tabs">
        <button :class="{ on: tab === 'camera' }" @click="openTab('camera')">Camera</button>
        <button :class="{ on: tab === 'library' }" @click="openTab('library')">Photo library</button>
      </div>

      <div v-if="tab === 'camera'" class="md-capture-camera">
        <div v-if="live" class="md-capture-live">
          <video ref="liveVideo" autoplay muted playsinline />
          <div class="md-capture-guides" />
          <p class="md-capture-hint">Take a photo. For a video, use “Record video”.</p>
        </div>
        <div v-else class="md-capture-nolive">
          <p>{{ liveError || 'Opening the camera…' }}</p>
        </div>
        <div class="md-row md-capture-controls">
          <button class="md-btn ghost" @click="$refs.videoInput.click()">● Record video</button>
          <button v-if="live" class="md-capture-shutter" aria-label="Take photo" @click="shoot" />
          <button v-else class="md-btn" @click="$refs.cameraInput.click()">Open phone camera</button>
          <span class="md-small md-muted">{{ picks.length }} taken</span>
        </div>
        <input ref="cameraInput" type="file" accept="image/*" capture="environment" multiple hidden @change="onFiles($event, 'camera')" />
        <input ref="videoInput" type="file" accept="video/*" capture="environment" hidden @change="onFiles($event, 'camera')" />
      </div>

      <div v-else class="md-capture-library">
        <button class="md-capture-drop" @click="$refs.libraryInput.click()" @dragover.prevent @drop.prevent="onDrop">
          <strong>Choose photos or videos</strong>
          <span class="md-muted md-small">JPG, PNG, HEIC, MP4 or MOV · select several at once, or drop them here</span>
        </button>
        <input ref="libraryInput" type="file" accept="image/*,video/*,.heic,.heif,.mov,.m4v" multiple hidden @change="onFiles($event, 'gallery')" />
      </div>

      <div v-if="picks.length" class="md-capture-picks">
        <div class="md-row"><strong>{{ picks.length }} item{{ picks.length === 1 ? '' : 's' }} selected</strong></div>
        <div class="md-capture-strip">
          <div v-for="p in picks" :key="p.key" class="md-capture-pick" :class="p.status">
            <img v-if="p.thumbUrl" :src="p.thumbUrl" alt="" />
            <span v-else class="md-capture-pick-state">{{ p.status === 'failed' ? '!' : '…' }}</span>
            <span v-if="p.processed && p.processed.kind === 'video'" class="md-capture-pick-kind">▶</span>
            <button class="md-capture-pick-x" :aria-label="`Remove ${p.file.name}`" @click="removePick(p)">×</button>
            <span v-if="p.status === 'failed'" class="md-capture-pick-err">{{ p.error }}</span>
          </div>
        </div>
      </div>
    </div>

    <div v-else>
      <div class="md-capture-strip small">
        <div v-for="p in ready" :key="p.key" class="md-capture-pick ready"><img :src="p.thumbUrl" alt="" /></div>
        <button class="md-capture-add" @click="step = 'pick'">+<br><span class="md-small">Add more</span></button>
      </div>
      <p class="md-small md-muted">{{ ready.length }} item{{ ready.length === 1 ? '' : 's' }} ready · capture time read from each file, location data removed.</p>

      <div v-if="asNote" class="md-field">
        <label for="md-cap-note">Note text *</label>
        <textarea id="md-cap-note" v-model="noteText" class="md-textarea" placeholder="What you saw. The note keeps these photos exactly as attached." />
      </div>

      <MediaTagForm v-model="form" :with-patient="!asNote" :note-label="asNote ? 'Caption on the photos' : 'Note'" />

      <label v-if="form.patient_unique_id !== null && ready.length > 1" class="md-capture-set">
        <input v-model="groupAsSet" type="checkbox" />
        <span>Group into a new set</span>
        <input v-if="groupAsSet" v-model="setName" class="md-input" maxlength="150" placeholder="Set name, e.g. Left leg bruise" @click.stop />
      </label>
    </div>

    <footer class="md-capture-foot">
      <span v-if="processing" class="md-small md-muted md-grow">Processing {{ processing }}…</span>
      <button class="md-btn ghost" @click="close">Cancel</button>
      <button v-if="step === 'pick'" class="md-btn" :disabled="!ready.length || processing > 0" @click="toTag">Next: details ({{ ready.length }})</button>
      <button v-else class="md-btn" :disabled="!canUpload" @click="upload">{{ busy ? 'Uploading…' : asNote ? `Save note with ${ready.length} item${ready.length === 1 ? '' : 's'}` : `Upload ${ready.length} item${ready.length === 1 ? '' : 's'}` }}</button>
    </footer>
  </section>
</template>

<script setup>
import { ref, computed, onBeforeUnmount, nextTick, onMounted } from 'vue';
import { useMediaStore } from '@/stores/mediaStore';
import { processMediaFile } from '@/utils/mediaPipeline';
import MediaTagForm from './MediaTagForm.vue';

const props = defineProps({
  asNote: { type: Boolean, default: false },
  patientUid: { type: String, default: null },
  layout: { type: String, default: 'page' },
  backLabel: { type: String, default: '' },
});
const emit = defineEmits(['back', 'done']);
const store = useMediaStore();

const step = ref('pick');
const tab = ref('camera');
const picks = ref([]);
const live = ref(false);
const liveError = ref('');
const liveVideo = ref(null);
const busy = ref(false);
const noteText = ref('');
const groupAsSet = ref(true);
const setName = ref('');
let stream = null;
let seq = 0;

const form = ref({
  patient_unique_id: props.patientUid !== null ? props.patientUid : store.lastPatientUid,
  note: '', primary_tag: null, tags: [], body_site: null, body_site_detail: '', orddiagid: null, patient_visible: 0,
  captured_by_unique_id: null,
});

const heading = computed(() => (step.value === 'pick' ? (props.asNote ? 'Note with photos' : 'Capture media') : props.asNote ? 'Write the note' : 'Upload media'));
const ready = computed(() => picks.value.filter((p) => p.status === 'ready'));
const processing = computed(() => picks.value.filter((p) => p.status === 'processing').length);
const canUpload = computed(() => !busy.value && ready.value.length > 0 && (!props.asNote || noteText.value.trim())
  && (!groupAsSet.value || ready.value.length < 2 || form.value.patient_unique_id === null || setName.value.trim()));

async function openTab(t) {
  tab.value = t;
  if (t === 'camera') await startCamera();
  else stopCamera();
}

async function startCamera() {
  if (live.value) return;
  liveError.value = '';
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    liveError.value = 'This browser can’t show a live camera here. Use the phone camera instead.';
    return;
  }
  try {
    stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment', width: { ideal: 2560 } }, audio: false });
    live.value = true;
    await nextTick();
    if (liveVideo.value) liveVideo.value.srcObject = stream;
  } catch (e) {
    liveError.value = 'No camera access (blocked, or none on this device). Use the phone camera, or the photo library.';
  }
}

function stopCamera() {
  if (stream) stream.getTracks().forEach((t) => t.stop());
  stream = null;
  live.value = false;
}

function shoot() {
  const v = liveVideo.value;
  if (!v || !v.videoWidth) return;
  const c = document.createElement('canvas');
  c.width = v.videoWidth;
  c.height = v.videoHeight;
  c.getContext('2d').drawImage(v, 0, 0);
  c.toBlob((blob) => {
    if (!blob) return;
    const now = Date.now();
    addFiles([new File([blob], `capture-${now}.jpg`, { type: 'image/jpeg', lastModified: now })], 'camera');
  }, 'image/jpeg', 0.95);
}

function onFiles(e, source) {
  addFiles([...(e.target.files || [])], source);
  e.target.value = '';
}
function onDrop(e) { addFiles([...((e.dataTransfer && e.dataTransfer.files) || [])], 'gallery'); }

function addFiles(files, source) {
  files.forEach((file) => {
    seq += 1;
    const p = { key: seq, file, source, status: 'processing', processed: null, thumbUrl: null, error: '' };
    picks.value.push(p);
    processMediaFile(file, source).then((out) => {
      const row = picks.value.find((x) => x.key === p.key);
      if (!row) return;
      row.processed = out;
      row.thumbUrl = URL.createObjectURL(out.thumb);
      row.status = 'ready';
    }).catch((err) => {
      const row = picks.value.find((x) => x.key === p.key);
      if (!row) return;
      row.status = 'failed';
      row.error = err.message || 'Could not process this file.';
    });
  });
}

function removePick(p) {
  if (p.thumbUrl) URL.revokeObjectURL(p.thumbUrl);
  picks.value = picks.value.filter((x) => x.key !== p.key);
}

function toTag() {
  stopCamera();
  if (!setName.value) {
    // Named after the earliest capture, which is also the set's date.
    const first = ready.value.map((p) => p.processed.captured_at).filter(Boolean).sort()[0];
    const d = (first ? new Date(first) : new Date()).toLocaleDateString([], { month: 'short', day: 'numeric' });
    setName.value = `Photos — ${d}`;
  }
  step.value = 'tag';
}

async function upload() {
  busy.value = true;
  const f = form.value;
  const out = await store.run((api) => api.commitMediaBatch({
    files: ready.value.map((p) => p.processed),
    patient_unique_id: props.asNote ? (props.patientUid || store.patientUid) : f.patient_unique_id,
    note: f.note, primary_tag: f.primary_tag, tags: f.tags, body_site: f.body_site, body_site_detail: f.body_site_detail,
    orddiagid: f.orddiagid, patient_visible: f.patient_visible, captured_by_unique_id: f.captured_by_unique_id,
    create_set: groupAsSet.value && ready.value.length > 1 && setName.value.trim() ? { name: setName.value.trim() } : null,
    source: props.asNote ? { pnotes: noteText.value.trim() } : null,
  }), {
    bump: true,
    success: (r) => `${r.items.length} item${r.items.length === 1 ? '' : 's'} ${props.asNote ? 'attached to a new note' : `added to ${f.patient_unique_id ? 'the patient’s media' : 'your library'}`}${r.set ? ` · set “${r.set.name}”` : ''}.`,
  });
  busy.value = false;
  if (out) {
    if (f.patient_unique_id) store.setPatient(f.patient_unique_id);
    release();
    emit('done', out);
  }
}

function release() {
  stopCamera();
  picks.value.forEach((p) => p.thumbUrl && URL.revokeObjectURL(p.thumbUrl));
}
function close() {
  release();
  emit('back');
}

onMounted(() => openTab(window.matchMedia && window.matchMedia('(max-width: 640px)').matches ? 'camera' : 'library'));
onBeforeUnmount(stopCamera);
</script>

<style scoped>
.md-capture { max-width:680px; background:#fff; }
.md-capture-head { display:flex; align-items:center; gap:10px; flex-wrap:wrap; padding-bottom:12px; }
.md-capture-head .md-back { flex-basis:100%; }
.md-capture-head h2 { font-size:22px; }
.md-capture-foot { display:flex; gap:10px; justify-content:flex-end; align-items:center; padding:12px 0; border-top:1px solid var(--line); margin-top:12px; position:sticky; bottom:0; background:#fff; }
/* Viewer first: a sheet over the library. */
.md-capture.is-sheet { width:100%; max-height:calc(100vh - 32px); overflow:auto; border-radius:14px; padding:16px 18px 0; box-shadow:0 10px 40px rgba(0,0,0,.25); }
.md-capture.is-sheet .md-capture-head .md-back { flex-basis:auto; }
.md-capture-tabs { display:flex; margin-bottom:14px; }
.md-capture-tabs button { flex:1; }
.md-capture-live { position:relative; background:#000; border-radius:12px; overflow:hidden; aspect-ratio:4 / 3; }
.md-capture-live video { width:100%; height:100%; object-fit:cover; display:block; }
.md-capture-guides { position:absolute; inset:14%; border:2px solid rgba(255,255,255,.55); border-radius:14px; pointer-events:none; }
.md-capture-hint { position:absolute; left:0; right:0; bottom:8px; margin:0; text-align:center; color:#fff; font-size:13px; text-shadow:0 1px 2px #000; }
.md-capture-nolive { background:#11181b; color:#cfd8da; border-radius:12px; aspect-ratio:4 / 3; display:flex; align-items:center; justify-content:center; padding:24px; text-align:center; }
.md-capture-controls { justify-content:space-between; padding:12px 4px; }
.md-capture-shutter { width:66px; height:66px; border-radius:50%; border:5px solid #fff; background:#e9ecee; box-shadow:0 0 0 2px var(--teal); cursor:pointer; }
.md-capture-shutter:active { background:#cfd8da; }
.md-capture-drop { width:100%; border:2px dashed var(--line); border-radius:12px; background:#fafbfb; padding:42px 16px; display:flex; flex-direction:column; gap:6px; align-items:center; cursor:pointer; color:var(--ink); }
.md-capture-drop:hover { border-color:var(--teal); }
.md-capture-picks { margin-top:16px; }
.md-capture-strip { display:flex; gap:10px; overflow-x:auto; padding:10px 0; }
.md-capture-pick { position:relative; flex:0 0 96px; height:96px; border-radius:10px; overflow:hidden; background:#2b3a40; border:2px solid transparent; }
.md-capture-strip.small .md-capture-pick { flex-basis:72px; height:72px; }
.md-capture-pick img { width:100%; height:100%; object-fit:cover; }
.md-capture-pick.failed { border-color:var(--bad); background:var(--badbg); flex-basis:180px; }
.md-capture-pick-state { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; color:#fff; font-size:22px; }
.md-capture-pick.failed .md-capture-pick-state { color:var(--bad); align-items:flex-start; padding-top:6px; }
.md-capture-pick-err { position:absolute; left:6px; right:6px; bottom:4px; font-size:11px; line-height:1.25; color:var(--bad); }
.md-capture-pick-kind { position:absolute; left:6px; bottom:6px; background:rgba(0,0,0,.6); color:#fff; border-radius:4px; padding:0 5px; font-size:11px; }
.md-capture-pick-x { position:absolute; top:4px; right:4px; width:22px; height:22px; border-radius:50%; border:none; background:rgba(0,0,0,.65); color:#fff; cursor:pointer; line-height:1; }
.md-capture-add { flex:0 0 72px; height:72px; border:2px dashed var(--line); border-radius:10px; background:#fff; color:var(--teal); font-size:20px; cursor:pointer; line-height:1.1; }
.md-capture-set { display:flex; flex-wrap:wrap; align-items:center; gap:10px; padding:10px 12px; border:1px solid var(--line); border-radius:8px; margin-top:12px; cursor:pointer; }
.md-capture-set input[type=checkbox] { width:18px; height:18px; }
.md-capture-set .md-input { flex:1 1 220px; }
</style>
